import { cn } from "@/lib/utils";
import React, { useState, useEffect } from "react";

function InputNumber({
  className,
  children,
  onChange,
  onBlur,
  value,
  ...otherProps
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [localValue, setLocalValue] = useState(value !== undefined && value !== null ? String(value) : "");

  // Sync with outer value changes (e.g. form resets or external updates)
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const numVal = parseFloat(String(value));
      const localNum = parseFloat(localValue.replace(",", "."));
      if (isNaN(numVal)) {
        setLocalValue("");
      } else if (numVal !== localNum || (value === 0 && localValue === "")) {
        setLocalValue(String(value));
      }
    } else {
      setLocalValue("");
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    setLocalValue(rawVal);

    if (onChange) {
      const normalizedValue = rawVal.replace(",", ".");
      const target = e.target;
      const originalValue = target.value;
      
      target.value = normalizedValue;
      onChange(e);
      target.value = originalValue;
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      const normalizedValue = localValue.replace(",", ".");
      const target = e.target;
      const originalValue = target.value;
      
      target.value = normalizedValue;
      onBlur(e);
      target.value = originalValue;
    }
  };

  return (
    <input
      type="text"
      className={cn(
        "py-1.5 px-3 rounded-lg border border-gray-100 mt-2 w-full h-[39px] focus:outline-primary-400 disabled:text-slate-500 text-black",
        className
      )}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        // Allow navigation/editing keys
        const allowedKeys = [
          "Backspace",
          "Delete",
          "Tab",
          "Escape",
          "Enter",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End"
        ];
        if (allowedKeys.includes(e.key)) {
          return;
        }

        // Allow only digits
        const isDigit = /^[0-9]$/.test(e.key);
        // Allow dot and comma, but only if they are not already present
        const isDecimalSeparator = e.key === "." || e.key === ",";
        const hasDecimalSeparator = localValue.includes(".") || localValue.includes(",");

        if (isDecimalSeparator && !hasDecimalSeparator) {
          return;
        }

        if (!isDigit) {
          e.preventDefault();
        }
      }}
      {...otherProps}
    >
      {children}
    </input>
  );
}

export default InputNumber;
